/**
 * @typedef {import('../contracts/filters.js').FilterDef} FilterDef
 * @typedef {import('../contracts/grid.js').RowAction} RowAction
 * @typedef {import('../contracts/file-selection.js').FileMeta} FileMeta
 * @typedef {import('../contracts/file-selection.js').FileSelectionSink} FileSelectionSink
 * @typedef {import('../contracts/file-selection.js').FileManagerClient} FileManagerClient
 * @typedef {import('../contracts/page.js').PageServices & { fileManagerClient: FileManagerClient }} FileManagerPageServices
 */

import { createUrlState } from '../core/routing/url-state.js';
import { createElement } from '../core/dom/helpers.js';
import { createFileBrowser } from '../features/file-browser/file-browser.js';
import { textFilterDriver } from '../modules/filter-bar/drivers/text-filter-driver.js';
import { selectFilterDriver } from '../modules/filter-bar/drivers/select-filter-driver.js';
import { multiselectFilterDriver } from '../modules/filter-bar/drivers/multiselect-filter-driver.js';
import { daterangeFilterDriver } from '../modules/filter-bar/drivers/daterange-filter-driver.js';
import { tagEditorDialog } from '../components/tag-editor/tag-editor.js';
import { createApiOptionsLoader } from '../core/options/api-options-loader.js';
import { createApiOptionsLookup } from '../core/options/api-options-lookup.js';
import { createFileMetadataFromUpload, FILE_KINDS } from '../core/media/file-metadata.js';
import { safeStorageGet } from '../core/storage/safe-storage.js';
import { confirmDialog } from '../components/confirm-dialog/confirm-dialog.js';
import { NotFoundError } from '../core/http/errors.js';
import { isAbortError } from '../core/http/is-abort-error.js';

/** Broad allowlist accept-string for the standalone upload input. */
const UPLOAD_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.zip,.doc,.docx,.xlsx';

/** safe-storage key for the persisted view preference. */
const VIEW_STORAGE_KEY = 'file-manager.view';

/**
 * File manager / media library page.
 *
 * Standalone (no sink): browse (tiles ⇄ table), upload, tag, bulk-delete.
 * Pick mode (`options.selectionSink`): adds an "Insert selected" affordance and
 * filters selectable items by `sink.accept`; hands the chosen files to the host.
 *
 * The browse machinery lives in the reusable `createFileBrowser` core; this page
 * supplies the admin glue (upload/delete/tag) as injected row/bulk actions and
 * drives a `browser.refresh()` after each mutation.
 *
 * @param {HTMLElement} container
 * @param {FileManagerPageServices} services
 * @param {{ selectionSink?: FileSelectionSink }} [options]
 * @returns {import('../contracts/module.js').MountedModule}
 */
export function mountFileManagerPage(container, services, options = {}) {
  const { apiClient, eventBus, logger, fileManagerClient } = services;
  const sink = options.selectionSink;
  const pickMode = sink != null;
  const multiple = sink ? sink.multiple !== false : true;
  const accept = sink?.accept ?? '';

  logger.info('File manager page initializing', { data: { pickMode } });

  let destroyed = false;

  // --- Page chrome NOT owned by browse: heading + upload control ---
  const heading = createElement('h1', {}, ['File manager']);
  const uploadBtn = createElement('button', { class: 'btn btn-primary', type: 'button' }, ['Upload file']);
  const uploadAccept = pickMode && sink?.accept ? sink.accept : UPLOAD_ACCEPT;
  /** @type {Record<string, string>} */
  const uploadInputAttrs = { type: 'file', accept: uploadAccept, style: 'display:none' };
  if (multiple) uploadInputAttrs.multiple = 'multiple';
  const uploadInput = /** @type {HTMLInputElement} */ (createElement('input', uploadInputAttrs));

  // --- Filters (declarative; full 4-filter set with all 4 drivers) ---
  const tagLoader = createApiOptionsLoader(apiClient, { url: '/file-tags/options', limit: 50 });
  const tagLookup = createApiOptionsLookup(apiClient, { url: '/file-tags/options' });

  /** @type {FilterDef[]} */
  const FILTERS = [
    { key: 'name', label: 'Search by file name', type: 'text', placeholder: 'Search by file name...', span: 4 },
    {
      key: 'kind', label: 'Type', type: 'select',
      options: FILE_KINDS.map(k => ({ value: k, label: k.charAt(0).toUpperCase() + k.slice(1) })),
    },
    { key: 'tags', label: 'Tags', type: 'multiselect', loadOptions: tagLoader, lookupOptions: tagLookup },
    { key: 'created', label: 'Created', type: 'daterange' },
  ];

  // ---------------------------------------------------------------------------
  // Admin glue (page-side) — upload, delete, tag editor.
  // ---------------------------------------------------------------------------

  /** @param {File} file */
  function uploadOne(file) {
    if (!services.uploadClient) {
      eventBus.emit('toast.message.requested', { message: 'Upload is not available', type: 'error' });
      return Promise.resolve();
    }
    logger.info('File upload start', { data: { name: file.name } });
    return services.uploadClient.upload(file, { signal: services.signal })
      .then((uploaded) => {
        if (destroyed) return null;
        const meta = createFileMetadataFromUpload(uploaded, file);
        return fileManagerClient.create(meta, { signal: services.signal })
          .then((row) => {
            logger.info('File upload success', { data: { id: row.id, name: file.name } });
            return row;
          })
          .catch((/** @type {unknown} */ err) => {
            if (isAbortError(err)) return null;
            logger.error('File metadata create failed after upload', { data: { url: uploaded.url, name: file.name } });
            eventBus.emit('toast.message.requested', {
              message: 'Upload saved but couldn’t be added to the library', type: 'error',
            });
            return null;
          });
      })
      .catch((/** @type {unknown} */ err) => {
        if (isAbortError(err)) return null;
        logger.error('File upload failed', { data: err });
        eventBus.emit('toast.message.requested', { message: 'Upload failed', type: 'error' });
        return null;
      })
      .then((row) => {
        if (destroyed) return;
        if (row) {
          eventBus.emit('toast.message.requested', { message: 'File uploaded', type: 'success' });
          browser.clearSelection();
          browser.refresh();
        }
      });
  }

  const onUploadClick = () => uploadInput.click();
  uploadBtn.addEventListener('click', onUploadClick);

  const onUploadChange = () => {
    const files = [...(uploadInput.files ?? [])];
    uploadInput.value = '';
    for (const f of files) uploadOne(f);
  };
  uploadInput.addEventListener('change', onUploadChange);

  /** @param {FileMeta[]} rows */
  function deleteFiles(rows) {
    if (rows.length === 0) return;
    confirmDialog({
      title: rows.length === 1 ? 'Delete file' : 'Delete files',
      message: `Delete ${rows.length} file${rows.length > 1 ? 's' : ''}? This cannot be undone.`,
    }).then((confirmed) => {
      if (!confirmed || destroyed) return;
      logger.info('File delete start', { data: { count: rows.length } });
      Promise.allSettled(rows.map(r =>
        fileManagerClient.remove(r.id, { signal: services.signal })
          .catch((/** @type {Error} */ err) => {
            if (err instanceof NotFoundError) return;
            throw err;
          })
      )).then((results) => {
        if (destroyed) return;
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) {
          logger.error('File delete failed', { data: { failed } });
          eventBus.emit('toast.message.requested', { message: 'Some files could not be deleted', type: 'error' });
        } else {
          logger.info('File delete success', { data: { count: rows.length } });
          eventBus.emit('toast.message.requested', { message: 'Files deleted', type: 'success' });
        }
        browser.clearSelection();
        browser.refresh();
      });
    });
  }

  /** @type {import('../components/tag-editor/tag-editor.js').TagEditorHandle | null} */
  let openTagDialog = null;

  /** @param {FileMeta} file */
  function openTagEditor(file) {
    if (openTagDialog) return; // single-instance guard
    const handle = tagEditorDialog({
      title: 'Edit tags',
      current: file.tags ?? [],
      loadTags: (s) => tagLoader({ query: '', values: {}, signal: s }),
      createTag: (name, s) => fileManagerClient.createTag(name, { signal: s }),
    });
    openTagDialog = handle;

    handle.promise.then((tags) => {
      openTagDialog = null;
      if (tags == null || destroyed) return;
      return fileManagerClient.updateTags(file.id, tags, { signal: services.signal }).then((updated) => {
        if (destroyed || !updated) return;
        logger.info('File tags updated', { data: { id: file.id } });
        eventBus.emit('toast.message.requested', { message: 'Tags updated', type: 'success' });
        browser.refresh();
      });
    }).catch((/** @type {unknown} */ err) => {
      openTagDialog = null;
      if (isAbortError(err)) return;
      logger.error('Tag update failed', { data: err });
      eventBus.emit('toast.message.requested', { message: 'Failed to update tags', type: 'error' });
    });
  }

  // ---------------------------------------------------------------------------
  // Inject the admin surface into the core. rowActions/onRowAction are passed
  // REGARDLESS of pick mode (parity with the previous page). Only the action-row
  // bulkActions differ: standalone = Edit-tags(single)/Delete; pick = Insert.
  // ---------------------------------------------------------------------------
  /** @type {RowAction[]} */
  const ROW_ACTIONS = [
    { key: 'edit-tags', label: 'Edit tags', icon: 'edit' },
    { key: 'delete', label: 'Delete', variant: 'danger', icon: 'delete' },
  ];

  /** @param {string} action @param {FileMeta} row */
  function onRowAction(action, row) {
    if (action === 'edit-tags') openTagEditor(row);
    else if (action === 'delete') deleteFiles([row]);
  }

  /** @type {import('../features/file-browser/file-browser.js').FileBrowserBulkAction[]} */
  const bulkActions = pickMode
    ? [{
      label: sink?.confirmLabel ?? 'Insert selected',
      variant: 'primary',
      onClick: (rows) => { if (sink) sink.onSelect(rows); },
    }]
    : [
      { label: 'Edit tags', singleOnly: true, onClick: (rows) => openTagEditor(rows[0]) },
      { label: 'Delete selected', variant: 'danger', onClick: (rows) => deleteFiles(rows) },
    ];

  const storedView = safeStorageGet(VIEW_STORAGE_KEY, 'table');

  const browser = createFileBrowser(container, {
    browseClient: fileManagerClient,
    filters: FILTERS,
    filterDrivers: [textFilterDriver, selectFilterDriver, multiselectFilterDriver, daterangeFilterDriver],
    urlState: createUrlState({ unorderedKeys: new Set(['tags']) }),
    logger,
    eventBus,
    signal: services.signal,
    localeConfig: services.localeConfig,
    selectable: true,
    multiple,
    accept,
    rowActions: ROW_ACTIONS,
    onRowAction,
    bulkActions,
    initialView: storedView === 'tiles' ? 'tiles' : 'table',
    persistView: true,
    viewStorageKey: VIEW_STORAGE_KEY,
    showViewToggle: true,
    headerExtras: [heading, uploadBtn, uploadInput],
  });

  logger.info('File manager page initialized');

  return {
    destroy() {
      destroyed = true;
      if (openTagDialog) { openTagDialog.destroy(); openTagDialog = null; }
      uploadBtn.removeEventListener('click', onUploadClick);
      uploadInput.removeEventListener('change', onUploadChange);
      browser.destroy();
    },
  };
}

export default mountFileManagerPage;
